import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "reactstrap";
import { toast } from "react-toastify";
import { ProductContext } from "../../../store";
import DetailSkeleton from "../../../components/Skeleton/DetailSkeleton";
import WritingPreviewHeader from "../../../components/AiVisibility/WritingAssistance/Preview/WritingPreviewHeader";
import WritingPreviewLayout from "../../../components/AiVisibility/WritingAssistance/Preview/WritingPreviewLayout";
import WritingPreviewProcessing from "../../../components/AiVisibility/WritingAssistance/Preview/WritingPreviewProcessing";
import WritingPreviewRawDiff from "../../../components/AiVisibility/WritingAssistance/Preview/WritingPreviewRawDiff";
import {
	getWritingAssistantJobPreview,
	getWritingAssistantJobStatus,
	isWritingJobComplete,
	pollWritingAssistantJob,
	regenerateWritingAssistantJob,
} from "../../../services/writingAssistantApi";

const WRITING_ASSISTANCE_PATH = "/aivisibility/writing-assistance";

function isJobNotFoundMessage(message) {
	const m = String(message || "").toLowerCase();
	return m.includes("job not found") || /\bnot found\b/.test(m);
}

function friendlyWritingError(message) {
	const cleaned = String(message || "")
		.replace(/\s*[—–-]\s*(GET|POST|PUT|DELETE|PATCH)\s+https?:\/\/\S+/gi, "")
		.replace(/https?:\/\/\S+/gi, "")
		.trim();
	return cleaned || "Preview not available.";
}

const AiVisibilityWritingView = () => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const jobId = searchParams.get("jobId");
	const { currentWebsiteId, userLoader, websiteLoader, setLoadVisualEditor } =
		useContext(ProductContext);

	useEffect(() => {
		setLoadVisualEditor(true);
		return () => setLoadVisualEditor(false);
	}, [setLoadVisualEditor]);

	const [job, setJob] = useState(null);
	const [phase, setPhase] = useState("idle");
	const [initialLoading, setInitialLoading] = useState(true);
	const [error, setError] = useState(null);
	const [processingLabel, setProcessingLabel] = useState(null);
	const [processingUrl, setProcessingUrl] = useState(null);
	const [isRegenerating, setIsRegenerating] = useState(false);

	/**
	 * Called by WritingPreviewLayout after savePreviewEdits finishes with
	 * `allowed=true` and returns a new preview object. We patch the current
	 * job's preview (plus any top-level fields like status / status_label
	 * returned by the pipeline) without re-running the whole load-job flow.
	 */
	const handlePreviewUpdated = useCallback(
		(nextPreview) => {
			if (!nextPreview) return;
			setJob((prevJob) => {
				if (!prevJob) return prevJob;
				const merged = {
					...prevJob,
					preview: {
						...(prevJob.preview || {}),
						...nextPreview,
					},
				};
				// Top-level overrides that the pipeline may have refreshed after
				// running again with user edits:
				[ "status", "status_label", "error_message" ].forEach((k) => {
					if (Object.prototype.hasOwnProperty.call(nextPreview, k)) {
						merged[k] = nextPreview[k];
					}
				});
				return merged;
			});
		},
		[]
	);

	const redirectJobUnavailable = useCallback(
		(message) => {
			toast.error(
				isJobNotFoundMessage(message)
					? "This preview is no longer available."
					: friendlyWritingError(message) || "This preview is no longer available."
			);
			navigate(WRITING_ASSISTANCE_PATH, { replace: true });
		},
		[navigate]
	);

	const loadJobFlow = useCallback(
		async (targetJobId) => {
			if (!targetJobId) {
				setJob(null);
				setPhase("idle");
				setInitialLoading(false);
				return;
			}

			if (!currentWebsiteId) {
				return;
			}

			setInitialLoading(true);
			setError(null);
			setJob(null);

			try {
				const statusResult = await getWritingAssistantJobStatus(
					currentWebsiteId,
					targetJobId
				);

				if (!statusResult.ok || !statusResult.job) {
					const msg = statusResult.message || "Preview not available.";
					if (isJobNotFoundMessage(msg)) {
						redirectJobUnavailable(msg);
						return;
					}
					setError(friendlyWritingError(msg));
					setPhase("error");
					return;
				}

				let currentJob = statusResult.job;
				setProcessingUrl(currentJob.url ?? null);

				if (!isWritingJobComplete(currentJob.status)) {
					setPhase("processing");
					setProcessingLabel(currentJob.status_label || "Processing");

					const poll = await pollWritingAssistantJob(currentWebsiteId, targetJobId, {
						onTick: (tickJob) => {
							if (tickJob?.status_label) setProcessingLabel(tickJob.status_label);
							if (tickJob?.url) setProcessingUrl(tickJob.url);
						},
					});

					if (!poll.ok) {
						const msg = poll.message || "Job failed";
						if (isJobNotFoundMessage(msg)) {
							redirectJobUnavailable(msg);
							return;
						}
						setError(friendlyWritingError(msg));
						setPhase("error");
						return;
					}

					currentJob = poll.job;
				}

				setPhase("loading-preview");
				setProcessingLabel("Loading preview");

				const previewResult = await getWritingAssistantJobPreview(
					currentWebsiteId,
					targetJobId
				);

				if (!previewResult.ok || !previewResult.job?.preview) {
					const msg = previewResult.message || "Preview not available.";
					if (isJobNotFoundMessage(msg)) {
						redirectJobUnavailable(msg);
						return;
					}
					setError(friendlyWritingError(msg));
					setPhase("error");
					return;
				}

				setJob(previewResult.job);
				setPhase("ready");
			} catch {
				setError("Failed to load preview.");
				setPhase("error");
			} finally {
				setInitialLoading(false);
				setIsRegenerating(false);
			}
		},
		[currentWebsiteId, redirectJobUnavailable]
	);

	useEffect(() => {
		if (userLoader || websiteLoader) return;
		if (!jobId) {
			setPhase("idle");
			setInitialLoading(false);
			return;
		}
		if (!currentWebsiteId) return;
		loadJobFlow(jobId);
	}, [jobId, currentWebsiteId, loadJobFlow, userLoader, websiteLoader]);

	const handleRegenerate = useCallback(async () => {
		if (!jobId || isRegenerating || !currentWebsiteId || phase === "processing") return;

		setIsRegenerating(true);

		try {
			const regen = await regenerateWritingAssistantJob(currentWebsiteId, jobId);

			if (!regen.ok || !regen.job?.id) {
				toast.error(regen.message || "Failed to regenerate job");
				setIsRegenerating(false);
				return;
			}

			setJob(null);
			setPhase("processing");
			setProcessingLabel(regen.job.status_label || "Queued");
			setProcessingUrl(regen.job.url ?? job?.url ?? null);
			setInitialLoading(false);
			setSearchParams({ jobId: String(regen.job.id) });
		} catch {
			toast.error("Failed to regenerate preview");
			setIsRegenerating(false);
		}
	}, [jobId, isRegenerating, currentWebsiteId, phase, setSearchParams, job?.url]);

	if (userLoader || websiteLoader) {
		return <DetailSkeleton />;
	}

	if (!jobId) {
		return (
			<div className="contentBlock ai-visibility-dashboard">
				<p className="text-muted">No job selected.</p>
				<Button color="primary" onClick={() => navigate(WRITING_ASSISTANCE_PATH)}>
					Back to Writing Assistance
				</Button>
			</div>
		);
	}

	if (jobId && !currentWebsiteId && phase !== "error") {
		return <DetailSkeleton />;
	}

	if (initialLoading && phase === "idle") {
		return <DetailSkeleton />;
	}

	if (phase === "processing" || phase === "loading-preview") {
		return (
			<WritingPreviewProcessing url={processingUrl} statusLabel={processingLabel} />
		);
	}

	if (phase === "error") {
		return (
			<div className="contentBlock ai-visibility-dashboard writing-preview-page">
				<p className="text-muted mb-3">
					{friendlyWritingError(error) || "Preview not available."}
				</p>
				<Button color="primary" onClick={() => navigate(WRITING_ASSISTANCE_PATH)}>
					Back to Writing Assistance
				</Button>
			</div>
		);
	}

	if (!job) {
		return <DetailSkeleton />;
	}

	const viewPreview = job?.preview;

	return (
		<div className="contentBlock ai-visibility-dashboard writing-preview-page">
			<WritingPreviewHeader
				url={job.url}
				status={job.status}
				statusLabel={job.status_label}
				isProcessing={isRegenerating || phase === "processing"}
				onRegenerate={handleRegenerate}
			/>
			<WritingPreviewLayout job={job} onPreviewUpdated={handlePreviewUpdated} />
			<WritingPreviewRawDiff
				originalMd={viewPreview?.original_md}
				patchedMd={viewPreview?.patched_md}
			/>
		</div>
	);
};

export default AiVisibilityWritingView;

